import React, { Component } from 'react';
import Grid from '@mui/material/Grid';
import { helpers } from '@simplybenefits/sb-utils';
import * as Services from '../../../services/index.js';
import Text from '../../../components/Text.js';
import Button from '../../../components/Button.js';
import ClassListItem from './ClassListItem.js';
import ContainerControlSite from '../../../components/Divs/ContainerControlSite.js';
import { colors } from '../../../theme/index.js';
import RateTable from './RateTable.js';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
const benefitMap = {
  'dependentLife': 'Dependent Life',
  'std': 'Short Term Disability',
  'virtualHealthcare': 'Virtual Healthcare',
  'criticalIllnessSpouse': 'Critical Illness (Spouse)',
  'criticalIllnessChild': 'Critical Illness (Child)',
  'criticalIllness': 'Critical Illness',
  'lifeInsurance': 'Life Insurance',
  'stopLoss': 'Stop Loss',
  'outOfProvince': 'Out Of Country',
  'ltd': 'Long Term Disability',
  'deathDismemberment': 'Accidental Death & Dismemberment',
  'financialAwareness': 'Financial Awareness'
};
class Pricing extends Component {
  state = {
    selectedClass: null,
    classIds: [],
    pricing: null
  };
  _findFirstValidClassDesign(classDesigns) {
    // Iterate through the object entries to find the first valid class design
    for (const [classID, classDesign] of Object.entries(classDesigns)) {
      if (classDesign.planDesignID !== null) {
        // Return the first valid class design
        return classID;
      }
    }

    // Return null if no valid class design is found
    return null;
  }
  componentDidMount() {
    const classIds = helpers.sortObjByTitle(this.props.classData);
    const firstValidClassID = this._findFirstValidClassDesign(this.props.classData);
    this.setState({
      classIds: [...classIds],
      selectedClass: firstValidClassID,
      pricing: this.props.designs[firstValidClassID][this.props.classData[firstValidClassID].planDesignID].pricing
    });
  }
  _selectClass = value => {
    this.setState({
      selectedClass: value,
      pricing: this.props.designs[value][this.props.classData[value].planDesignID].pricing
    });
  };

  /**
   * Combines Health related rates
   * @param {*} pricing 
   * @param {*} coverageType 
   * @param {*} rateType 
   * @returns 
   */
  _getHealthRate = (pricing, coverageType, rateType) => {
    if (rateType === 'markup') {
      return pricing.drugs[coverageType].netRateWithMarkup - pricing.drugs[coverageType].netRate + (pricing.majorMedical[coverageType].netRateWithMarkup - pricing.majorMedical[coverageType].netRate) + (pricing.paramedicals[coverageType].netRateWithMarkup - pricing.paramedicals[coverageType].netRate) + (pricing.eyeExams[coverageType].netRateWithMarkup - pricing.eyeExams[coverageType].netRate) + (pricing.eyeGlassesAndContacts[coverageType].netRateWithMarkup - pricing.eyeGlassesAndContacts[coverageType].netRate) + (pricing.requiredMedicalEyewear[coverageType].netRateWithMarkup - pricing.requiredMedicalEyewear[coverageType].netRate);
    }
    return pricing.drugs[coverageType][rateType] + pricing.majorMedical[coverageType][rateType] + pricing.paramedicals[coverageType][rateType] + pricing.eyeExams[coverageType][rateType] + pricing.eyeGlassesAndContacts[coverageType][rateType] + pricing.requiredMedicalEyewear[coverageType][rateType];
  };

  /**
   * Combines Dental related rates
   * @param {*} pricing 
   * @param {*} coverageType 
   * @param {*} rateType 
   * @returns 
   */
  _getDentalRate = (pricing, coverageType, rateType) => {
    if (rateType === 'markup') {
      return pricing.basicDental[coverageType].netRateWithMarkup - pricing.basicDental[coverageType].netRate + (pricing.majorDental[coverageType].netRateWithMarkup - pricing.majorDental[coverageType].netRate) + (pricing.orthodontics[coverageType].netRateWithMarkup - pricing.orthodontics[coverageType].netRate);
    }
    return pricing.basicDental[coverageType][rateType] + pricing.majorDental[coverageType][rateType] + pricing.orthodontics[coverageType][rateType];
  };

  /**
   * Combines Efap related rates
   * @param {*} pricing 
   * @param {*} coverageType 
   * @param {*} rateType 
   * @returns 
   */
  _getEfapRate = (pricing, coverageType, rateType) => {
    if (rateType === 'markup') {
      return pricing.employeeAssistance[coverageType].netRateWithMarkup - pricing.employeeAssistance[coverageType].netRate + (pricing.medicalSecondOpinion[coverageType].netRateWithMarkup - pricing.medicalSecondOpinion[coverageType].netRate) + (pricing.eldercare[coverageType].netRateWithMarkup - pricing.eldercare[coverageType].netRate);
    }
    return pricing.employeeAssistance[coverageType][rateType] + pricing.medicalSecondOpinion[coverageType][rateType] + pricing.eldercare[coverageType][rateType];
  };

  /**
   * Creates Rate CSV
   * Rates are split into solo, duo, family rows per grouped benefit type
   */
  _downloadCSV = () => {
    const {
      designs,
      classData
    } = this.props;
    const csvRows = [];
    Object.keys(classData).forEach(classID => {
      const planDesignID = classData[classID].planDesignID;

      // Modflex parent class will be null
      if (planDesignID) {
        const pricing = designs[classID][planDesignID].pricing;

        // Create Extended Health Care rows (Combines Drugs, Major Medical, Paramedicals, Eye Exams, Eye Glasses And Contacts, and Required Medical Eyewear)
        ['solo', 'duo', 'family'].forEach(coverageType => {
          csvRows.push({
            title: classData[classID].title,
            benefitType: 'Extended Health Care',
            coverageType,
            netRate: helpers.formatNumber(this._getHealthRate(pricing, coverageType, 'netRate'), 3),
            markup: helpers.formatNumber(this._getHealthRate(pricing, coverageType, 'markup'), 3),
            commissionRate: helpers.formatNumber(this._getHealthRate(pricing, coverageType, 'commissionRate'), 3),
            grossRate: helpers.formatNumber(this._getHealthRate(pricing, coverageType, 'grossRate'), 3)
          });
        });

        // Create Dental rows (Combines Major Dental, Basic Dental, and Orthodontics)
        ['solo', 'duo', 'family'].forEach(coverageType => {
          csvRows.push({
            title: classData[classID].title,
            benefitType: 'Dental',
            coverageType,
            netRate: helpers.formatNumber(this._getDentalRate(pricing, coverageType, 'netRate'), 3),
            markup: helpers.formatNumber(this._getDentalRate(pricing, coverageType, 'markup'), 3),
            commissionRate: helpers.formatNumber(this._getDentalRate(pricing, coverageType, 'commissionRate'), 3),
            grossRate: helpers.formatNumber(this._getDentalRate(pricing, coverageType, 'grossRate'), 3)
          });
        });

        // Create Efap rows (Combines Employee Assistance, Medical Second Opinion, and Eldercare)
        ['solo', 'duo', 'family'].forEach(coverageType => {
          csvRows.push({
            title: classData[classID].title,
            benefitType: 'Employee & Family Assistance Program (EFAP)',
            coverageType,
            netRate: helpers.formatNumber(this._getEfapRate(pricing, coverageType, 'netRate'), 3),
            markup: helpers.formatNumber(this._getEfapRate(pricing, coverageType, 'markup'), 3),
            commissionRate: helpers.formatNumber(this._getEfapRate(pricing, coverageType, 'commissionRate'), 3),
            grossRate: helpers.formatNumber(this._getEfapRate(pricing, coverageType, 'grossRate'), 3)
          });
        });

        // Filter out benefits with rows created above.
        const excludedBenefits = ['drugs', 'majorMedical', 'paramedicals', 'eyeExams', 'eyeGlassesAndContacts', 'requiredMedicalEyewear', 'majorDental', 'basicDental', 'orthodontics', 'employeeAssistance', 'medicalSecondOpinion', 'eldercare'];
        const filteredPricingArray = Object.entries(pricing).filter(([key]) => !excludedBenefits.includes(key));

        // Create rows from remaining benefits
        filteredPricingArray.forEach(([benefitType, benefitRates]) => {
          ['solo', 'duo', 'family'].forEach(coverageType => {
            const coverageTypeRates = benefitRates[coverageType];
            csvRows.push({
              title: classData[classID].title,
              benefitType: benefitMap[benefitType],
              coverageType,
              netRate: helpers.formatNumber(coverageTypeRates.netRate, 3),
              markup: helpers.formatNumber(coverageTypeRates.netRateWithMarkup - coverageTypeRates.netRate, 3),
              commissionRate: helpers.formatNumber(coverageTypeRates.commissionRate, 3),
              grossRate: helpers.formatNumber(coverageTypeRates.grossRate, 3)
            });
          });
        });
      }
    });
    const options = {
      title: 'Rate Summary',
      fieldSeparator: ',',
      filename: `Rate Summary-${this.props.currentAccountMeta.accountID}-${new Date().toLocaleDateString()}`,
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      useTextFile: false,
      useBom: true,
      headers: ['Class', 'Benefit', 'Coverage Type', 'Net Rate', 'Markup Rate', 'Commission Rate', 'Gross Rate']
    };
    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(csvRows);
  };
  render() {
    const {
      classData,
      currentAccountMeta
    } = this.props;
    const styles = {
      contentBlock: {
        backgroundColor: colors.surface,
        boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
        display: 'block',
        margin: 'auto auto 30px auto',
        padding: '30px',
        position: 'relative',
        zIndex: 3
      },
      downloadBtnContainer: {
        display: 'flex',
        flex: 1,
        justifyContent: 'flex-end',
        marginBottom: 10
      }
    };
    if (!this.state.selectedClass || !this.state.pricing) {
      return null;
    }
    return /*#__PURE__*/React.createElement(ContainerControlSite, null, /*#__PURE__*/React.createElement("div", {
      style: styles.downloadBtnContainer
    }, /*#__PURE__*/React.createElement(Button, {
      transparent: true,
      onClick: () => this.props.downloadPricingPdf("PlanPricing", "Plan-Pricing.pdf", {
        accountID: currentAccountMeta.accountID
      })
    }, i18n.t(tokens.buttons.downloadPdf)), this.props.isMasterSite && /*#__PURE__*/React.createElement(Button, {
      transparent: true,
      onClick: () => this._downloadCSV()
    }, i18n.t(tokens.buttons.downloadCsv))), /*#__PURE__*/React.createElement(Grid, {
      container: true,
      alignItems: "center",
      spacing: 2
    }, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 4,
      sx: {
        paddingBottom: "8px"
      }
    }, /*#__PURE__*/React.createElement(Text, {
      align: "center",
      mediumLarge: true,
      display: "inline"
    }, i18n.t(tokens.titles.class)))), /*#__PURE__*/React.createElement(Grid, {
      container: true,
      spacing: 2
    }, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      sm: 2,
      md: 3
    }, this.state.classIds.map((classID, i) => {
      // remove mod flex classes that have no plan design, ie parent classes
      if (this.props.classData[classID].planDesignID === null) {
        return null;
      }
      return /*#__PURE__*/React.createElement(ClassListItem, {
        id: i === 0 ? 'class' : `class-${i}`,
        key: Services.concatTokens(classID, classData[classID].title),
        data: classData[classID],
        onSelect: () => this._selectClass(classID),
        selected: this.state.selectedClass === classID
      });
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      sm: 10,
      md: 9
    }, /*#__PURE__*/React.createElement("div", {
      style: styles.contentBlock
    }, /*#__PURE__*/React.createElement(RateTable, {
      pricing: this.state.pricing,
      isAsoDental: currentAccountMeta.isAsoDental,
      isAsoHealth: currentAccountMeta.isAsoHealth
    })))));
  }
}
export default Pricing;